import mixpanel from 'mixpanel-browser';

// Mixpanel is initialised on demand by the cookie consent provider
// (see `shared/consent/useConsent.tsx`) the first time the user accepts.
// Until then it stays silent — every `analytics.trackX(...)` call is a
// no-op so that nothing is tracked and no Mixpanel cookie / localStorage
// `mp_*` key is written. This is the ePrivacy "prior consent" rule.
let isInitialized = false;
let initAttempted = false;

export function initMixpanel(): void {
  if (isInitialized || initAttempted) return;
  initAttempted = true;
  try {
    const token = process.env.REACT_APP_MIXPANEL_TOKEN;
    if (!token) {
      console.warn('Mixpanel token not provided');
      return;
    }
    mixpanel.init(token, {
      debug: process.env.REACT_APP_MIXPANEL_DEBUG === 'true',
      track_pageview: true,
      persistence: 'localStorage',
      api_host: 'https://api-eu.mixpanel.com',
    });
    isInitialized = true;
  } catch (error) {
    console.error('Mixpanel initialization failed:', error);
  }
}

export const analytics = {
  // The client-side 'Login' event was removed 2026-07-29: it duplicated the
  // server-side Login (backend/routes/auth.js) under the same event name,
  // double-counting every login. The client only identifies (setUserId).

  // Track user signup (client-side) - complements server-side signup tracking
  trackSignupFrontend: (userId: string) => {
    if (!isInitialized) return;
    
    try {
      mixpanel.track('Signup Frontend', {
        distinct_id: userId
      });
    } catch (error) {
      console.error('Analytics tracking error (Signup Frontend):', error);
    }
  },
  
  /**
   * A click on a header link (components/Header.tsx). Same event name and
   * cta_name values as the landing page header ('Header Plan a trip',
   * 'Header Menu Login', ...), told apart by `source`: 'app' here,
   * 'landing-page' there. Browser-side because a header click makes no API
   * call. Docs: docs/analytics-tracking.md §5e.
   */
  trackCTAClick: (ctaName: string, destination: string) => {
    if (!isInitialized) return;

    try {
      mixpanel.track('CTA Click', {
        cta_name: ctaName,
        destination,
        source: 'app',
      });
    } catch (error) {
      console.error('Analytics tracking error (CTA Click):', error);
    }
  },

  // Set user ID for cross-session tracking
  setUserId: (userId: string) => {
    if (!isInitialized) return;

    try {
      mixpanel.identify(userId);
    } catch (error) {
      console.error('Analytics user identification error:', error);
    }
  },

  /**
   * Internal-traffic filter, browser half (the server half lives in
   * backend/services/analytics.js). Call on every identify with the
   * server-computed `user.isInternal`.
   *
   * Uses the SDK's own opt-out rather than a guard inside each track()
   * wrapper, because opt-out is the only thing that also suppresses
   * Mixpanel's AUTOCAPTURED `[Auto] Page View` events — our wrappers never
   * see those. Opting back IN for a normal user matters too: the flag
   * persists in localStorage, so without it a real user on a machine that
   * once logged in as admin would stay silently untracked forever.
   *
   * Docs: docs/analytics-tracking.md §6.
   */
  setInternalUser: (isInternal: boolean) => {
    if (!isInitialized) return;
    try {
      if (isInternal) {
        if (!mixpanel.has_opted_out_tracking()) mixpanel.opt_out_tracking();
      } else if (mixpanel.has_opted_out_tracking()) {
        mixpanel.opt_in_tracking();
      }
    } catch (error) {
      console.error('Analytics internal-user opt-out error:', error);
    }
  },

  // Reset user on logout
  reset: () => {
    if (!isInitialized) return;
    
    try {
      mixpanel.reset();
    } catch (error) {
      console.error('Analytics reset error:', error);
    }
  },

  // `affiliate_link_clicked` used to fire here. It moved SERVER-SIDE (the
  // affiliate-link endpoint in backend/routes/journeys.js) so ad blockers
  // can't drop the revenue-funnel entry event; the client now just passes
  // ?source= and ?daysUntilDeparture= on the link fetch. See
  // docs/integrations/omio-impact.md and docs/integrations/mixpanel.md.

  /**
   * "Describe a place": the user opened a Google search for one of Jody's
   * suggested places (StopPlacePicker). Client-side: the link makes no API
   * call. Joins to the server's stop_suggestions_requested / _picked events
   * by llm_request_id. Tells us how often a one-line reason is not enough.
   */
  trackStopSuggestionLookedUp: (properties: {
    action: 'add' | 'replace';
    rank: number;
    suggestion_count: number;
    city: string;
    llm_request_id: string | null;
  }) => {
    if (!isInitialized) return;
    try {
      mixpanel.track('stop_suggestion_looked_up', properties);
    } catch (error) {
      console.error('Analytics tracking error (stop_suggestion_looked_up):', error);
    }
  },

  /**
   * Leg sheet opened (tap on a leg card or its Planned pill). One of the few
   * client-side events of the 2026-07 instrumentation pass: opening the sheet
   * triggers no API call, so there is no server hook — accepts the ad-blocker
   * loss until the first-party proxy backlog item lands.
   */
  trackLegDetailsViewed: (properties: {
    journey_id: string;
    leg_id: string;
    transport_mode: string | null;
    planned: boolean;
    source: 'card_tap' | 'pill_tap';
  }) => {
    if (!isInitialized) return;
    try {
      mixpanel.track('leg_details_viewed', properties);
    } catch (error) {
      console.error('Analytics tracking error (leg_details_viewed):', error);
    }
  },

  /**
   * Compare-transport-options dialog opened (stage 'options'). Client-side of
   * necessity: the /compare endpoint is PREFETCHED on every sheet open for the
   * mode chips, so a server hook there would count sheet opens instead of
   * compare usage, and opening the dialog itself makes no dedicated request.
   * The companion stage 'prices' event fires server-side on /compare-prices.
   */
  trackLegTransportCompared: (properties: {
    journey_id: string;
    leg_id: string;
    current_mode: string | null;
  }) => {
    if (!isInitialized) return;
    try {
      mixpanel.track('leg_transport_compared', { ...properties, stage: 'options' });
    } catch (error) {
      console.error('Analytics tracking error (leg_transport_compared):', error);
    }
  },

  /**
   * Travel Impact Calculator: successful calculation event.
   *
   * Fired client-side (not server-side) so Mixpanel auto-enriches the
   * event with the visitor's anonymous distinct_id, `$city`, `$country_code`,
   * `$os`, `$browser`, screen size, current URL, and referrer. Server-side
   * firing was producing empty events (no auto-enrichment available
   * once you're outside the browser SDK) and rolling everyone into
   * `distinct_id: 'anonymous'` killed the ability to compute funnels.
   *
   * Privacy: no email, no user ID, no PII. Mixpanel's distinct_id is
   * an anonymous cookie/localStorage value scoped to this browser; it
   * is never linked to an account in our DB.
   *
   * Properties:
   *  - `origin`, `destination`: **city name only** as the user
   *    selected from the geocoder ("Berlin", "Rome"). Kept short so
   *    dashboards group by city without trailing region/country noise.
   *  - `originCountry`, `destinationCountry`: country name from the
   *    same geocoder result, or `null` when the provider didn't
   *    return one. Surfaced separately so charts can pivot by
   *    country without parsing strings.
   *  - `mode`: chosen TransportMode.
   *  - `roundTrip`: boolean. Kept as a flag (not folded into kg/km)
   *    so the analyst can halve to one-way if needed.
   *  - `distanceKm`, `co2eKg`: **one-way** detour-adjusted values.
   *    The chart row carries the RT-doubled numbers; analytics keeps
   *    modifiers separate from base units so cross-trip averages
   *    stay honest. Pair with `roundTrip` to fold ×2 if needed.
   *  - `segmentCount`: total modes compared in the batch (4 when the
   *    user picked a default mode, 5 when they picked van/motorbike/ferry).
   *
   * Property shape is a superset of the old server-side event, so
   * dashboards built on the previous shape keep working.
   */
  trackTravelImpactCalculatorCalculate: (properties: {
    origin: string;
    originCountry: string | null;
    destination: string;
    destinationCountry: string | null;
    mode: string;
    roundTrip: boolean;
    distanceKm: number;
    co2eKg: number;
    segmentCount: number;
  }) => {
    if (!isInitialized) return;
    try {
      mixpanel.track('travel_impact_calculator_calculate', properties);
    } catch (error) {
      console.error('Analytics tracking error (travel_impact_calculator_calculate):', error);
    }
  },

  /**
   * Travel Impact Calculator: 429 quota_exceeded event. Fired from
   * the calculator page's error handler when the public batch
   * endpoint refuses a calc with `code: 'quota_exceeded'`.
   *
   * Privacy + rationale: same as `trackTravelImpactCalculatorCalculate`.
   * Note that the underlying rate-limit decision *is* server-side
   * (the backend has the per-IP counter, not the browser); this event
   * only records that the browser observed the gate, which is what
   * matters for funnel analysis ("how many calc attempts get gated").
   */
  trackTravelImpactCalculatorQuotaGateHit: (properties: { retryAfterSeconds: number }) => {
    if (!isInitialized) return;
    try {
      mixpanel.track('travel_impact_calculator_quota_gate_hit', properties);
    } catch (error) {
      console.error(
        'Analytics tracking error (travel_impact_calculator_quota_gate_hit):',
        error
      );
    }
  },

  // ──────────────────────────────────────────────────────────────────────────
  // Travel Impact Calculator: share flow
  //
  // One event per method, plus a "button click" event for the dialog
  // open (which captures intent even when the user abandons without
  // picking a channel). Per-method events make it easy to read "which
  // channel is winning" off Mixpanel without a property breakdown,
  // at the cost of needing a union across 5 events to compute a
  // grand-total "shares fired" number.
  //
  // Each event accepts a free-form `properties` object so callers
  // can pass placement + trip context (mode, roundTrip, chosenKg,
  // placement). Mixpanel auto-attaches the visitor's anonymous
  // distinct_id, GeoIP city/country, OS/browser, and current URL on
  // top of whatever the caller passes.
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Fires when the user clicks the Share button (opens the share
   * dialog). Treat as a "share intent" signal. Followed by a method
   * event when the user picks a channel, or by nothing when they
   * abandon — that gap is the dialog-abandonment rate.
   */
  trackTravelImpactCalculatorShareButtonClick: (properties: Record<string, unknown> = {}) => {
    trackShareEvent('travel_impact_calculator_share_button_click', properties);
  },

  /** Fires when the user copies the share payload to the clipboard. */
  trackTravelImpactCalculatorShareClipboard: (properties: Record<string, unknown> = {}) => {
    trackShareEvent('travel_impact_calculator_share_clipboard', properties);
  },

  /** Fires when the user copies only the share link (Copy link, 2026-10-03). */
  trackTravelImpactCalculatorShareLinkClipboard: (properties: Record<string, unknown> = {}) => {
    trackShareEvent('travel_impact_calculator_share_link_clipboard', properties);
  },

  /** Fires when the user opens the email composer (`mailto:`). */
  trackTravelImpactCalculatorShareEmail: (properties: Record<string, unknown> = {}) => {
    trackShareEvent('travel_impact_calculator_share_email', properties);
  },

  /** Fires when the user shares via WhatsApp (`wa.me`). */
  trackTravelImpactCalculatorShareWhatsApp: (properties: Record<string, unknown> = {}) => {
    trackShareEvent('travel_impact_calculator_share_whatsapp', properties);
  },

  /** Fires when the user shares via X / Twitter (`twitter.com/intent/tweet`). */
  trackTravelImpactCalculatorShareX: (properties: Record<string, unknown> = {}) => {
    trackShareEvent('travel_impact_calculator_share_x', properties);
  },

  /** Fires when the user picks the Web Share API (mobile native sheet). */
  trackTravelImpactCalculatorShareWebShare: (properties: Record<string, unknown> = {}) => {
    trackShareEvent('travel_impact_calculator_share_web_share', properties);
  },

  // ──────────────────────────────────────────────────────────────────────────
  // Trip sharing v1 (client-side slice). The owner's state changes and both
  // conversion actions fire SERVER-side (trip_share_updated,
  // trip_share_edit_requested, journey_copied — see backend/services/
  // analytics.js); these three cover the moments with no API call, or with
  // anonymous viewers where only the browser SDK has a usable distinct_id
  // (the calculator precedent, docs/analytics-tracking.md §7).
  // ──────────────────────────────────────────────────────────────────────────

  /** A share dialog opened (share intent; no API call). `role` separates the
   *  owner's dialog from a VISITOR resharing a shared trip — the second hop
   *  of the viral loop. */
  trackTripShareDialogOpened: (properties: { trip_id: string; source: 'trip_detail' | 'journey_detail' | 'my_trips' | 'shared_trip' | 'shared_journey'; role: 'owner' | 'visitor' }) => {
    trackShareEvent('trip_share_dialog_opened', properties);
  },

  /** A share channel was used from a share dialog (see role above). channel:
   *  copy_link | copy (the message) | email | whatsapp | x | web_share; email
   *  carries email_method 'app' here. A Jody send ('jody') fires server-side. */
  trackTripShareChannelClicked: (properties: { trip_id: string; channel: string; role: 'owner' | 'visitor'; email_method?: 'app' }) => {
    trackShareEvent('trip_share_channel_clicked', properties);
  },

  /** A visitor opened a shared trip view (/s/<token>) — the reach metric of
   *  the share loop. Anonymous distinct_id by design. */
  trackTripShareLinkViewed: (properties: { trip_id: string; visibility: string; signed_in: boolean; surface: 'trip' | 'journey' }) => {
    trackShareEvent('trip_share_link_viewed', properties);
  },

  /**
   * A visitor of a shared trip clicked a booking button (the shared view's
   * twin of the server-side `affiliate_link_clicked`, which needs a user id).
   * Same event name and properties, `viewer: 'shared_visitor'`, anonymous
   * distinct_id. Catalog: docs/analytics-tracking.md §5.
   */
  trackSharedAffiliateLinkClicked: (properties: {
    partner: 'omio' | 'booking' | 'operator';
    /** Night trains: which operator runs the train, whatever the partner. */
    operator_key?: string | null;
    operator_name?: string | null;
    journey_id: string;
    leg_id?: string;
    transport_mode?: string | null;
    from_city?: string | null;
    to_city?: string | null;
    stop_id?: string;
    city?: string | null;
    source: 'timeline' | 'leg_sheet' | 'stop_sheet';
    days_until_departure?: number | null;
  }) => {
    trackShareEvent('affiliate_link_clicked', { ...properties, viewer: 'shared_visitor' });
  },

  /**
   * Travel Impact Calculator: signup CTA click event.
   *
   * Successor of the retired `travel_impact_calculator_waitlist_submit`
   * (the waitlist sunset Aug 2026): the calculator's conversion surfaces
   * now send visitors straight to /signup, so the measurable moment on
   * this page is the click. Fired client-side from `SignupCTAButton`
   * right before the SPA navigation, so:
   *  - the same anonymous `distinct_id` Mixpanel assigned to the
   *    visitor on page load carries through, and funnels (page view →
   *    calculate → quota_gate → signup_click → Signup Frontend)
   *    compute correctly;
   *  - Mixpanel's auto-collected city / country / OS / browser ride
   *    along.
   * The completed signup itself is measured downstream (`Signup
   * Frontend` + the server-side Signup event + the Google Ads
   * conversion in `shared/gtag.ts`).
   *
   * Properties:
   *  - `source`: which surface hosted the button
   *    (`travel_impact_calculator_main`, `_cant_train_dialog`,
   *    `_quota_gate`).
   *  - Trip context when available (origin, destination, mode,
   *    roundTrip, co2eKg, distanceKm). Forwarded by the calculator
   *    page so the analyst can answer "what trip do people who click
   *    through to signup tend to be calculating."
   */
  trackTravelImpactCalculatorSignupClick: (properties: {
    source: string;
    [key: string]: unknown;
  }) => {
    if (!isInitialized) return;
    try {
      mixpanel.track('travel_impact_calculator_signup_click', properties);
    } catch (error) {
      console.error(
        'Analytics tracking error (travel_impact_calculator_signup_click):',
        error
      );
    }
  },
};

/**
 * Internal helper for share-family events. Centralises the
 * try/catch + isInitialized guard so the six wrapper functions
 * above can stay one-liners and the contract for "shouldn't crash if
 * Mixpanel isn't initialized" is enforced in one place.
 */
function trackShareEvent(eventName: string, properties: Record<string, unknown>) {
  if (!isInitialized) return;
  try {
    mixpanel.track(eventName, properties);
  } catch (error) {
    console.error(`Analytics tracking error (${eventName}):`, error);
  }
}

// Expose mixpanel globally for debugging in development (after analytics is defined)
if (process.env.NODE_ENV === 'development' && isInitialized) {
  (window as any).mixpanel = mixpanel;
  (window as any).analytics = analytics;
  console.log('🔧 Mixpanel exposed globally for debugging (development only)');
} 